import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Calendar, BarChart3, Download, FileText, Activity, User, Clock, Eye, QrCode, Share2, TrendingUp, Loader2, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import {
  getCompanyAnalytics,
  getProfileAnalyticsSummary,
  getAuditLog,
  type CompanyAnalyticsView,
  type ProfileAnalyticsSummaryView,
  type ProfileAuditLogView,
  ProfileAuditAction,
} from '../../../api/PublicProfile/publicProfileApi';

interface AnalyticsAuditTabProps {
  selectedPersonaId: string;
  onSettingsChange?: () => void;
}

const AnalyticsAuditTab: React.FC<AnalyticsAuditTabProps> = ({ selectedPersonaId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last-30-days');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [companyAnalytics, setCompanyAnalytics] = useState<CompanyAnalyticsView | null>(null);
  const [profileSummaries, setProfileSummaries] = useState<ProfileAnalyticsSummaryView[]>([]);
  const [auditLogs, setAuditLogs] = useState<ProfileAuditLogView[]>([]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const getDateRangeParams = useCallback(() => {
    const now = new Date();
    let startDate: Date | undefined;

    switch (dateRange) {
      case 'last-7-days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'last-30-days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last-90-days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'last-year':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'all-time':
        startDate = undefined;
        break;
    }

    return {
      startDate: startDate?.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    };
  }, [dateRange]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeParams();

      const [analyticsResult, summariesResult, auditResult] = await Promise.all([
        getCompanyAnalytics(startDate, endDate),
        getProfileAnalyticsSummary(startDate, endDate),
        getAuditLog(100, 0),
      ]);

      if (analyticsResult.status === 200 && analyticsResult.payload) {
        setCompanyAnalytics(analyticsResult.payload);
      }

      if (summariesResult.status === 200 && summariesResult.payload) {
        setProfileSummaries(summariesResult.payload);
      }

      if (auditResult.status === 200 && auditResult.payload) {
        setAuditLogs(auditResult.payload);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      showToast('error', t('Failed to load analytics'));
    } finally {
      setLoading(false);
    }
  }, [getDateRangeParams, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExportAnalytics = () => {
    if (!profileSummaries.length) return;

    const headers = ['Employee', 'Profile Slug', 'Views', 'Shares', 'Contacts', 'QR Scans', 'Last View'];
    const rows = profileSummaries.map(p => [
      p.employeeName,
      p.profileSlug || '-',
      p.totalViews.toString(),
      p.totalShares.toString(),
      p.totalContacts.toString(),
      p.totalQRScans.toString(),
      p.lastViewDate || 'Never',
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profile-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAuditLog = () => {
    if (!auditLogs.length) return;

    const headers = ['Timestamp', 'Action', 'Employee', 'Profile', 'Actor', 'Field Changed', 'Old Value', 'New Value'];
    const rows = auditLogs.map(log => [
      new Date(log.createdOn).toLocaleString(),
      log.action,
      log.employeeName || '-',
      log.profileSlug || '-',
      log.actorName || 'System',
      log.fieldChanged || '-',
      log.oldValue || '-',
      log.newValue || '-',
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getActionLabel = (action: ProfileAuditAction) => {
    const labels: Record<string, string> = {
      [ProfileAuditAction.Created]: 'Profile Created',
      [ProfileAuditAction.Updated]: 'Profile Updated',
      [ProfileAuditAction.Published]: 'Profile Published',
      [ProfileAuditAction.Unpublished]: 'Profile Unpublished',
      [ProfileAuditAction.SlugRegenerated]: 'Slug Regenerated',
      [ProfileAuditAction.ApprovalRequested]: 'Approval Requested',
      [ProfileAuditAction.Approved]: 'Profile Approved',
      [ProfileAuditAction.Rejected]: 'Profile Rejected',
      [ProfileAuditAction.Deleted]: 'Profile Deleted',
    };
    return labels[action] || action;
  };

  const getActionCategory = (action: ProfileAuditAction) => {
    if ([ProfileAuditAction.Approved, ProfileAuditAction.Rejected, ProfileAuditAction.ApprovalRequested].includes(action)) {
      return 'Approval';
    }
    if ([ProfileAuditAction.Published, ProfileAuditAction.Unpublished].includes(action)) {
      return 'Visibility';
    }
    if (action === ProfileAuditAction.SlugRegenerated) {
      return 'Security';
    }
    return 'Profile Change';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ms-3 text-muted-foreground">{t('Loading analytics...')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2
                        ${toast.type === 'success'
                          ? 'bg-success/10 text-success'
                          : 'bg-destructive/10 text-destructive'}`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Persona Info Banner */}
      {selectedPersonaId && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            <p className="text-sm text-primary">
              {t('Analytics for the selected persona. Select a different persona to view its analytics.')}
            </p>
          </div>
        </div>
      )}

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card variant="elevated">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Total Profile Views')}</p>
                <p className="text-3xl font-bold text-foreground mt-2">{companyAnalytics?.totalViews || 0}</p>
                <p className="text-xs text-success mt-1 flex items-center">
                  <TrendingUp className="w-3 h-3 me-1" />
                  {t('Active tracking')}
                </p>
              </div>
              <Eye className="w-10 h-10 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('QR Code Scans')}</p>
                <p className="text-3xl font-bold text-foreground mt-2">{companyAnalytics?.totalQRScans || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('Via mobile devices')}
                </p>
              </div>
              <QrCode className="w-10 h-10 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Profile Shares')}</p>
                <p className="text-3xl font-bold text-foreground mt-2">{companyAnalytics?.totalShares || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('Link copies')}
                </p>
              </div>
              <Share2 className="w-10 h-10 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Contact Submissions')}</p>
                <p className="text-3xl font-bold text-foreground mt-2">{companyAnalytics?.totalContactSubmissions || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t(`${companyAnalytics?.activeProfiles || 0} of ${companyAnalytics?.totalProfiles || 0} profiles active`)}
                </p>
              </div>
              <MessageSquare className="w-10 h-10 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Date Range Filter */}
      <Card variant="elevated">
        <CardContent>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground flex items-center">
              <Calendar className="w-5 h-5 me-2" />
              {t('Analytics Period')}
            </h2>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <option value="last-7-days">{t('Last 7 Days')}</option>
              <option value="last-30-days">{t('Last 30 Days')}</option>
              <option value="last-90-days">{t('Last 90 Days')}</option>
              <option value="last-year">{t('Last Year')}</option>
              <option value="all-time">{t('All Time')}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Top Viewed Profiles */}
      {companyAnalytics?.topViewedProfiles && companyAnalytics.topViewedProfiles.length > 0 && (
        <Card variant="elevated">
          <CardContent>
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 me-2" />
              {t('Top Viewed Profiles')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {companyAnalytics.topViewedProfiles.map((profile, index) => (
                <div key={profile.profileId} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{profile.employeeName}</p>
                      <p className="text-sm text-muted-foreground">{profile.viewCount} {t('views')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Performance Table */}
      <Card variant="elevated">
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center">
              <BarChart3 className="w-5 h-5 me-2" />
              {t('Profile Performance')}
            </h2>
            <Button
              variant="outline"
              onClick={handleExportAnalytics}
              disabled={profileSummaries.length === 0}
              iconName="Download"
              className="min-h-[44px]"
            >
              {t('Export Report')}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Employee')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Views')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('QR Scans')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Shares')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Contacts')}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Last Viewed')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {profileSummaries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                      {t('No profile analytics data available')}
                    </td>
                  </tr>
                ) : (
                  profileSummaries.map((profile) => (
                    <tr key={profile.profileId} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-foreground">{profile.employeeName}</div>
                          {profile.profileSlug && (
                            <div className="text-xs text-muted-foreground">/p/{profile.profileSlug}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Eye className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-semibold text-foreground">{profile.totalViews}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <QrCode className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-semibold text-foreground">{profile.totalQRScans}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Share2 className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-semibold text-foreground">{profile.totalShares}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <MessageSquare className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-semibold text-foreground">{profile.totalContacts}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-muted-foreground">
                          {profile.lastViewDate ? new Date(profile.lastViewDate).toLocaleDateString() : t('Never')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card variant="elevated">
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center">
              <FileText className="w-5 h-5 me-2" />
              {t('Admin Audit Log')}
            </h2>
            <Button
              variant="outline"
              onClick={handleExportAuditLog}
              disabled={auditLogs.length === 0}
              iconName="Download"
              className="min-h-[44px]"
            >
              {t('Export Log')}
            </Button>
          </div>

          <div className="space-y-3">
            {auditLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('No audit log entries yet')}</p>
              </div>
            ) : (
              auditLogs.map((log) => (
                <div key={log.id} className="flex items-start gap-4 p-4 border border-border rounded-lg hover:bg-muted/50">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Activity className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-foreground">
                        {t(getActionLabel(log.action))}
                        {log.employeeName && <span className="text-muted-foreground"> - {log.employeeName}</span>}
                      </p>
                      <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                        {t(getActionCategory(log.action))}
                      </span>
                    </div>
                    {log.fieldChanged && (
                      <p className="text-xs text-muted-foreground mb-1">
                        {t('Field')}: {log.fieldChanged}
                        {log.oldValue && <span> | {t('Old')}: {log.oldValue}</span>}
                        {log.newValue && <span> | {t('New')}: {log.newValue}</span>}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.actorName || t('System')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(log.createdOn).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsAuditTab;
